// ─────────────────────────────────────────────────────────────────────────────
// Sidebar icons.
//
// One small line icon per navigation section, used only so the sidebar stays
// legible when collapsed to an icon rail. Hand-drawn inline SVG — no icon
// library dependency for twenty glyphs. Purely presentational: this file knows
// nothing about routing or permissions, it only maps a section id to a shape.
// ─────────────────────────────────────────────────────────────────────────────

import type { ReactElement } from "react";

function Svg({ children }: { readonly children: ReactElement | readonly ReactElement[] }): ReactElement {
  return (
    <svg viewBox="0 0 20 20" width="18" height="18" fill="none" stroke="currentColor"
      strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {children}
    </svg>
  );
}

const ICONS: Readonly<Record<string, ReactElement>> = {
  myDashboard: <Svg><path d="M3 10 10 3.5 17 10 M5.5 9V17H14.5V9 M8.3 17V12H11.7V17" /></Svg>,
  profile: <Svg><circle cx="10" cy="7" r="3.2" /><path d="M4 17c0-3.5 3-6 6-6s6 2.5 6 6" /></Svg>,
  notifications: <Svg><path d="M6 14V9a4 4 0 0 1 8 0v5l1.5 2h-11z" /><path d="M8.3 17.5a1.8 1.8 0 0 0 3.4 0" /></Svg>,
  myPerformance: <Svg><path d="M3 15 8 9 11.5 12 17 5" /><path d="M13 5h4v4" /></Svg>,
  calendar: <Svg><rect x="3" y="4.5" width="14" height="12.5" rx="1.5" /><path d="M3 8.5h14 M6.5 2.5v4 M13.5 2.5v4" /></Svg>,
  tasks: <Svg><path d="M4 6h2 M4 10h2 M4 14h2 M8 6h8 M8 10h8 M8 14h8" /></Svg>,
  attendance: <Svg><circle cx="10" cy="10" r="7" /><path d="M10 6v4l3 2" /></Svg>,
  leave: <Svg><path d="M17 3 3 9.5l5.5 2L11 17l2-5.5L17 3z M8.5 11.5 17 3" /></Svg>,
  requests: <Svg><path d="M3 11h4l1.5 2h3L13 11h4" /><path d="M3 11V6a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1v5" /><path d="M3 11v4a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-4" /></Svg>,
  dashboard: <Svg><rect x="4" y="11" width="3" height="6" rx="0.5" /><rect x="8.5" y="7" width="3" height="10" rx="0.5" /><rect x="13" y="3" width="3" height="14" rx="0.5" /></Svg>,
  employees: <Svg><circle cx="7" cy="6.5" r="2.5" /><path d="M2.5 17c0-3 2-5 4.5-5s4.5 2 4.5 5" /><circle cx="14.2" cy="7.5" r="2" /><path d="M12.3 12.2c.5-.14 1-.2 1.7-.2 2.1 0 3.8 1.9 3.8 4.5v.5" /></Svg>,
  payroll: <Svg><rect x="2.5" y="6" width="15" height="8" rx="1.2" /><circle cx="10" cy="10" r="2" /><path d="M5 8h.01 M15 12h.01" /></Svg>,
  documents: <Svg><path d="M6 2.5h6l4 4V17a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V3.5a1 1 0 0 1 1-1z" /><path d="M12 2.5V7h4" /><path d="M7.5 11h5 M7.5 14h5" /></Svg>,
  loans: <Svg><ellipse cx="10" cy="5.5" rx="6" ry="2.2" /><path d="M4 5.5v4c0 1.2 2.7 2.2 6 2.2s6-1 6-2.2v-4" /><path d="M4 9.5v4c0 1.2 2.7 2.2 6 2.2s6-1 6-2.2v-4" /></Svg>,
  mail: <Svg><rect x="2.5" y="4.5" width="15" height="11" rx="1.2" /><path d="M3 5.5 10 11.5 17 5.5" /></Svg>,
  performance: <Svg><path d="M10 17a7 7 0 1 1 4.95-2.05" /><path d="M10 11V6 M10 11l4-3.5" /></Svg>,
  reports: <Svg><rect x="5" y="3.5" width="10" height="14" rx="1.2" /><rect x="8" y="2" width="4" height="2.5" rx="0.7" /><path d="M7.5 9h5 M7.5 12h5 M7.5 15h3" /></Svg>,
  orgchart: <Svg><rect x="7.5" y="2.5" width="5" height="3" rx="0.6" /><rect x="2.5" y="14.5" width="5" height="3" rx="0.6" /><rect x="12.5" y="14.5" width="5" height="3" rx="0.6" /><path d="M10 5.5v4 M10 9.5H5v5 M10 9.5h5v5" /></Svg>,
  assistant: <Svg><path d="M10 3 11.3 6.7 15 8 11.3 9.3 10 13 8.7 9.3 5 8 8.7 6.7 10 3z" /><path d="M15.5 12.5 16.1 14.2 17.8 14.8 16.1 15.4 15.5 17.1 14.9 15.4 13.2 14.8 14.9 14.2 15.5 12.5z" /></Svg>,
  mailSettings: <Svg><path d="M3 5h9 M3 10h5 M3 15h9" /><circle cx="14" cy="5" r="1.3" /><circle cx="10" cy="10" r="1.3" /><circle cx="14" cy="15" r="1.3" /></Svg>,
  settings: <Svg><circle cx="10" cy="10" r="2.6" /><path d="M10 3v2.2 M10 14.8V17 M17 10h-2.2 M5.2 10H3 M15.1 4.9l-1.5 1.5 M6.4 13.6l-1.5 1.5 M15.1 15.1l-1.5-1.5 M6.4 6.4 4.9 4.9" /></Svg>,
};

const FALLBACK = <Svg><circle cx="10" cy="10" r="6.5" /></Svg>;

export function NavIcon({ id }: { readonly id: string }): ReactElement {
  return ICONS[id] ?? FALLBACK;
}

export function ChevronIcon({ direction }: { readonly direction: "start" | "end" }): ReactElement {
  return (
    <svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor"
      strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
      style={{ transform: direction === "end" ? "rotate(180deg)" : undefined }}
    >
      <path d="M12 4 6 10l6 6" />
    </svg>
  );
}
